import React from "react";

import IconButton from "@replo/design-system/components/button/IconButton";
import twMerge from "@replo/design-system/utils/twMerge";
import { PencilRuler } from "lucide-react";
import { animated, config, useSpring } from "react-spring";

import ProductHostingTypeSelector from "./ProductHostingTypeSelector";

type SetupOption = "new" | "import" | "ai" | "themeBuilder" | "import-later";

interface OnboardingSetupShopContentProps {
  selectedHostingType: SetupOption | null;
  setSelectedHostingType: (option: SetupOption) => void;
  selectedProduct: SetupOption | null;
  setSelectedProduct: (option: SetupOption) => void;
  isShopContext?: boolean;
}

const OnboardingSetupShopContent: React.FC<OnboardingSetupShopContentProps> = ({
  selectedHostingType,
  setSelectedHostingType,
  selectedProduct,
  setSelectedProduct,
  isShopContext = false,
}) => {
  const isMobile = window.innerWidth < 768;

  const isImportMode =
    selectedHostingType === "import" || selectedHostingType === "import-later";
  const slideUpTransform =
    isImportMode && !isMobile ? "translateY(-100px)" : "translateY(0px)";
  const fadeInTransform =
    isImportMode && !isMobile ? "translateY(-130px)" : "translateY(0px)";

  const slideUpAnimation = useSpring({
    config: config.gentle,
    transform: slideUpTransform,
    opacity: 1,
  });

  const fadeInAnimation = useSpring({
    config: config.gentle,
    opacity: isImportMode ? 1 : 0,
    transform: fadeInTransform,
  });

  return (
    <div className="space-y-4 md:space-y-6">
      <ProductHostingTypeSelector
        selectedHostingType={selectedHostingType}
        setSelectedHostingType={setSelectedHostingType}
        setSelectedProduct={setSelectedProduct}
        isShopContext={isShopContext}
        slideUpAnimation={slideUpAnimation}
        size="sm"
      />

      {(selectedHostingType === "import" ||
        selectedHostingType === "import-later") && (
        <animated.div
          style={fadeInAnimation}
          className="flex flex-col gap-3 mt-6 md:mt-8"
        >
          <div className="text-left text-md font-medium">
            How would you like to build your pages?
          </div>
          <div className="flex flex-col md:flex-row gap-4 md:gap-8">
            <div
              className={twMerge(
                "flex-1 h-40 border rounded-xl p-4 group cursor-pointer overflow-hidden relative transition-colors duration-300",
                selectedProduct === "ai"
                  ? "border-transparent"
                  : "border-border",
              )}
              onClick={() => setSelectedProduct("ai")}
            >
              <img
                src="/images/onboarding/step3-bg.png"
                alt=""
                className={twMerge(
                  "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
                  selectedProduct === "ai" ? "opacity-100" : "opacity-0",
                )}
              />
              <div className="flex flex-col justify-center h-full relative z-10">
                <div
                  className={twMerge(
                    "flex flex-col gap-1",
                    isShopContext ? "!text-left" : "",
                  )}
                  {...(isShopContext && { "data-card-content": true })}
                >
                  <IconButton
                    variant="primary"
                    UNSAFE_className="!bg-black"
                    icon={
                      <img
                        src="/replo-logo-new-small.svg"
                        alt="Replo Logo"
                        className="w-[18px] h-[16px]"
                      />
                    }
                    size="base"
                  />
                  <div
                    className={twMerge(
                      "text-lg font-medium transition-colors duration-300 pt-1",
                      selectedProduct === "ai" ? "" : "text-muted",
                    )}
                    style={selectedProduct === "ai" ? { color: "#F5FAD9" } : {}}
                  >
                    AI Builder
                  </div>
                  <div
                    className={twMerge(
                      "font-light transition-colors duration-300 max-w-[250px]",
                      selectedProduct === "ai" ? "" : "text-placeholder",
                    )}
                    style={selectedProduct === "ai" ? { color: "#F5FAD9" } : {}}
                  >
                    Build pages from ideas and images—no code required.
                  </div>
                </div>
                <div className="absolute -bottom-70 left-130 right-0 flex items-end justify-center pointer-events-none">
                  <img
                    src="/images/onboarding/replo-chat.svg"
                    className="w-[500px] h-[550px] max-w-none max-h-none object-contain transform translate-x-3 -translate-y-15 transition-transform duration-400 ease-in-out group-hover:-translate-x-12"
                  />
                </div>
              </div>
            </div>
            <div
              className={twMerge(
                "flex-1 h-40 border rounded-xl p-4 group cursor-pointer overflow-hidden relative transition-colors duration-300",
                selectedProduct === "themeBuilder"
                  ? "border-transparent"
                  : "border-border",
              )}
              onClick={() => setSelectedProduct("themeBuilder")}
            >
              <img
                src="/images/onboarding/step3-bg.png"
                alt=""
                className={twMerge(
                  "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
                  selectedProduct === "themeBuilder"
                    ? "opacity-100"
                    : "opacity-0",
                )}
              />
              <div className="absolute -bottom-85 left-130 right-0 flex items-end justify-center pointer-events-none">
                <img
                  src="/images/onboarding/landing-page-builder.svg"
                  className="w-[500px] h-[500px] max-w-none max-h-none object-contain transform translate-x-3 -translate-y-15 transition-transform duration-400 ease-in-out group-hover:-translate-y-18"
                />
              </div>
              <div className="flex flex-col justify-center gap-6 h-full relative z-10">
                <div
                  className={twMerge(
                    "flex flex-col gap-1",
                    isShopContext ? "!text-left" : "",
                  )}
                  {...(isShopContext && { "data-card-content": true })}
                >
                  <IconButton
                    variant="secondary"
                    UNSAFE_className="pt-1 bg-sheet"
                    icon={<PencilRuler className="w-[18px] h-[16px]" />}
                    size="base"
                  />
                  <div
                    className={twMerge(
                      "text-lg font-medium transition-colors duration-300",
                      selectedProduct === "themeBuilder" ? "" : "text-muted",
                    )}
                    style={
                      selectedProduct === "themeBuilder"
                        ? { color: "#F5FAD9" }
                        : {}
                    }
                  >
                    Classic Landing Page Builder
                  </div>
                  <div
                    className={twMerge(
                      "font-light transition-colors duration-300 max-w-[250px]",
                      selectedProduct === "themeBuilder"
                        ? ""
                        : "text-placeholder",
                    )}
                    style={
                      selectedProduct === "themeBuilder"
                        ? { color: "#F5FAD9" }
                        : {}
                    }
                  >
                    Fine-tuned control for your Shopify theme.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </animated.div>
      )}
    </div>
  );
};

export default OnboardingSetupShopContent;
export type { SetupOption };
